import React, { useEffect, useRef, useState } from "react";
import { Camera, RefreshCcw, FastForward } from "lucide-react";

/** PhotoCapture — simple getUserMedia with a Skip-photo (DEV) option. */
export default function PhotoCapture({ onChange, onSkip, allowSkip = true }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [snapshot, setSnapshot] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: 640, height: 640 },
          audio: false,
        });
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          await videoRef.current.play();
          setReady(true);
        }
      } catch (e) {
        setError("Camera unavailable");
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const snap = () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const c = document.createElement("canvas");
    const side = Math.min(v.videoWidth, v.videoHeight);
    c.width = c.height = side;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, (v.videoWidth - side) / 2, (v.videoHeight - side) / 2, side, side, 0, 0, side, side);
    const url = c.toDataURL("image/jpeg", 0.85);
    setSnapshot(url);
    onChange?.(url);
  };
  const retake = () => {
    setSnapshot(null);
    onChange?.(null);
  };

  return (
    <div data-testid="photo-capture">
      <div className="aspect-square w-full max-w-[320px] mx-auto rounded-2xl overflow-hidden bg-black/40 border border-white/15 relative">
        {!snapshot ? (
          <video
            ref={videoRef}
            playsInline
            muted
            className="w-full h-full object-cover"
            data-testid="photo-video"
          />
        ) : (
          <img src={snapshot} alt="captured" className="w-full h-full object-cover" data-testid="photo-snapshot" />
        )}
        {!ready && !snapshot && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">Initialising camera…</div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center text-red-200 text-sm bg-red-900/40">
            {error}
          </div>
        )}
      </div>
      <div className="flex gap-3 justify-center mt-4 flex-wrap">
        {!snapshot ? (
          <button onClick={snap} disabled={!ready} className="inline-flex items-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-5 py-3 font-semibold" data-testid="photo-snap-btn" type="button">
            <Camera className="w-4 h-4" /> Take photo
          </button>
        ) : (
          <button onClick={retake} className="inline-flex items-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 text-white px-5 py-3" data-testid="photo-retake-btn" type="button">
            <RefreshCcw className="w-4 h-4" /> Retake
          </button>
        )}
        {allowSkip && !snapshot && (
          <button onClick={onSkip} className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 hover:bg-white/10 text-white px-4 py-3 text-sm" data-testid="photo-skip-btn" type="button">
            <FastForward className="w-4 h-4" /> Skip photo (DEV)
          </button>
        )}
      </div>
    </div>
  );
}
